import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { useI18n } from '../i18n/I18nProvider'
import type { MessageKey } from '../i18n/messages'
import { DateLisible, Nombre } from '../components/Nombre'
import { Badge, Card, DataTable, Grid, PageHeader, Stack, useToast } from '../components/ui'
import type { Column } from '../components/ui'
import { growthApi } from './growthApi'
import type { PlatformDashboard } from './growthApi'

/** Une tuile de chiffre : un nombre qui se lit seul, pas un graphique. */
function Tuile({ titre, children, detail }: { titre: string; children: React.ReactNode; detail?: React.ReactNode }) {
  return (
    <div className="rounded-app border px-4 py-3" style={{ background: 'var(--surface, #fff)' }}>
      <div className="text-muted" style={{ fontSize: '0.8rem' }}>{titre}</div>
      <div style={{ fontSize: '1.6rem', fontWeight: 700, lineHeight: 1.3 }}>{children}</div>
      {detail !== undefined && <div className="text-muted" style={{ fontSize: '0.8rem' }}>{detail}</div>}
    </div>
  )
}

const ORDRE_ETATS = ['active', 'trial', 'past_due', 'suspended', 'cancelled', 'expired']

/**
 * Le tableau de bord du super-administrateur (migration 0081).
 *
 * DES NOMBRES, PAS DES GRAPHIQUES : chaque indicateur est un chiffre qui se lit seul — revenu
 * récurrent, impayés, encaissé — et les répartitions sont des listes courtes, lues mieux en
 * tableau qu'en camembert. Tout est calculé en base, au jour de l'horloge du serveur.
 *
 * LE MRR NE COMPTE QUE CE QUI PAIE : abonnements actifs ou en attente de paiement, options
 * comprises, remise déduite, annuel ramené au mois. Un essai ne rapporte rien encore ; un
 * suspendu ne rapporte plus.
 */
export function DashboardPage() {
  const { t, describeError } = useI18n()
  const { session } = useAuth()
  const { notify } = useToast()
  const [d, setD] = useState<PlatformDashboard | null>(null)

  const token = session?.token ?? ''

  useEffect(() => {
    if (token === '') return
    const controle = new AbortController()
    growthApi
      .dashboard(token, controle.signal)
      .then(setD)
      .catch((cause: unknown) => {
        if (!controle.signal.aborted) notify(describeError(cause), 'erreur')
      })
    return () => controle.abort()
  }, [token, notify, describeError])

  if (d === null) return <p className="text-muted">{t('common.loading')}</p>

  const colonnesFormules: Column<PlatformDashboard['byPlan'][number]>[] = [
    { key: 'name', header: t('growth.dash.plan'), render: (p) => p.name },
    { key: 'clients', header: t('growth.dash.clients'), numeric: true, render: (p) => <Nombre valeur={p.clients} decimales={0} /> },
    { key: 'mrr', header: t('growth.dash.mrr'), numeric: true, render: (p) => <Nombre valeur={p.mrr} decimales={3} /> },
  ]

  const colonnesEssais: Column<PlatformDashboard['trialsEnding'][number]>[] = [
    { key: 'name', header: t('billing.col.client'), render: (e) => <Link to={`/platform/clients/${e.organizationId}`}>{e.name}</Link> },
    { key: 'endsOn', header: t('growth.dash.trialEnds'), render: (e) => <DateLisible valeur={e.endsOn} /> },
  ]

  const etats = [...ORDRE_ETATS.filter((s) => s in d.byStatus), ...Object.keys(d.byStatus).filter((s) => !ORDRE_ETATS.includes(s))]

  return (
    <Stack>
      <PageHeader title={t('growth.dash.title')} description={t('growth.dash.description')} />

      <Grid min="13rem">
        <Tuile titre={t('growth.dash.mrr')} detail={<>{t('growth.dash.arr')} : <Nombre valeur={d.arr} decimales={0} unite="TND" /></>}>
          <Nombre valeur={d.mrr} decimales={0} unite="TND" />
        </Tuile>
        <Tuile titre={t('growth.dash.clients')} detail={<>{t('growth.dash.churn30')} : <Nombre valeur={d.churn30} decimales={0} /></>}>
          <Nombre valeur={d.clients} decimales={0} />
        </Tuile>
        <Tuile titre={t('growth.dash.outstanding')} detail={<>{t('growth.dash.overdue')} : <Nombre valeur={d.overdue.amount} decimales={0} unite="TND" /> (<Nombre valeur={d.overdue.count} decimales={0} />)</>}>
          <Nombre valeur={d.outstanding.amount} decimales={0} unite="TND" />
        </Tuile>
        <Tuile titre={t('growth.dash.collectedMonth')} detail={<>{t('growth.dash.collectedYear')} : <Nombre valeur={d.collectedYear} decimales={0} unite="TND" /></>}>
          <Nombre valeur={d.collectedMonth} decimales={0} unite="TND" />
        </Tuile>
        <Tuile titre={t('growth.dash.renewals30')} detail={<Nombre valeur={d.renewals30.amount} decimales={0} unite="TND" />}>
          <Nombre valeur={d.renewals30.count} decimales={0} />
        </Tuile>
        <Tuile titre={t('growth.dash.openRequests')} detail={<Link to="/platform/requests">{t('growth.requests.title')}</Link>}>
          <Nombre valeur={d.openRequests} decimales={0} />
        </Tuile>
      </Grid>

      <Card title={t('growth.dash.byStatus')}>
        <div className="flex flex-wrap gap-2">
          {etats.map((s) => (
            <Badge key={s} tone={s === 'active' ? 'succes' : s === 'past_due' ? 'avertissement' : s === 'suspended' ? 'erreur' : 'info'}>
              {t(`platform.subscription.${s}` as MessageKey)} · <Nombre valeur={d.byStatus[s] ?? 0} decimales={0} />
            </Badge>
          ))}
        </div>
      </Card>

      <Stack>
        <Card title={t('growth.dash.byPlan')}>
          <DataTable columns={colonnesFormules} rows={d.byPlan} rowKey={(p) => p.code} emptyLabel="—" />
        </Card>
        <Card title={t('growth.dash.trialsEnding')}>
          <DataTable columns={colonnesEssais} rows={d.trialsEnding} rowKey={(e) => e.organizationId} emptyLabel={t('growth.dash.noTrials')} />
        </Card>
      </Stack>
    </Stack>
  )
}
